import { oc } from '@orpc/contract'
import { z } from 'zod'
import { creditsResetModeSchema } from './credits'

export const signupModeSchema = z.enum(['invite_only', 'domain_restricted'])

/** Per-organization feature flags (super-admin). */
export const organizationFeaturesSchema = z.object({
  /** PLAN-083: the assistant may drive the shared browser. */
  agentBrowser: z.boolean(),
  /** PLAN-084: the project inbox (email address, rules, agent runs). */
  inbox: z.boolean(),
  /** PLAN-085: HUB-hosted video meetings with live transcription. */
  realtimeMeetings: z.boolean(),
  /** Board wwfu: project plugins (remote MCP servers per project). */
  mcpPlugins: z.boolean(),
  /** Board t5xf: interactive tables and UI blocks in chat replies. */
  uiBlocks: z.boolean(),
})
export type OrganizationFeatures = z.infer<typeof organizationFeaturesSchema>

export const adminOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  prefix: z.string().nullable(),
  projectLimit: z.number(),
  signupMode: signupModeSchema,
  allowedEmailDomain: z.string().nullable(),
  requiredAuthProvider: z.enum(['google', 'microsoft']).nullable(),
  isActive: z.boolean(),
  features: organizationFeaturesSchema,
  /** Credits (PLAN credits-usage): budget, reset behaviour and current spend. */
  creditLimit: z.number(),
  creditsReset: creditsResetModeSchema,
  creditsAnchorDay: z.number(),
  creditsUsed: z.number(),
  creditsPeriodEnd: z.string().nullable(),
  memberCount: z.number(),
  projectCount: z.number(),
  createdAt: z.string(),
})

/**
 * Global settings (all organizations and projects), super-admin › Tools.
 * Missing rows are defaults, so every key is always present in the output.
 */
export const appSettingsSchema = z.object({
  /** Jam (jam.dev) bug reports: the SPA loads the recorder scripts. Default on. */
  jam: z.boolean(),
})
export type AppSettings = z.infer<typeof appSettingsSchema>

export const adminGetAppSettingsContract = oc
  .input(z.void())
  .output(z.object({ settings: appSettingsSchema }))

export const adminUpdateAppSettingsContract = oc
  .input(appSettingsSchema.partial())
  .output(z.object({ settings: appSettingsSchema }))

export const adminCheckContract = oc.input(z.void()).output(z.object({ isSuperAdmin: z.boolean() }))

export const adminListOrganizationsContract = oc
  .input(z.void())
  .output(z.object({ organizations: z.array(adminOrganizationSchema) }))

export const adminGetOrganizationContract = oc
  .input(z.object({ organizationId: z.string().min(1) }))
  .output(z.object({ organization: adminOrganizationSchema }))

export const adminCreateOrganizationContract = oc
  .input(
    z.object({
      name: z.string().min(1).max(100),
      projectLimit: z.number().int().min(1).default(10),
      signupMode: signupModeSchema.default('invite_only'),
      allowedEmailDomain: z.string().nullable().default(null),
      requiredAuthProvider: z.enum(['google', 'microsoft']).nullable().default(null),
    })
  )
  .output(z.object({ organization: adminOrganizationSchema }))

export const adminUpdateOrganizationContract = oc
  .input(
    z.object({
      organizationId: z.string().min(1),
      name: z.string().min(1).max(100).optional(),
      projectLimit: z.number().int().min(1).optional(),
      signupMode: signupModeSchema.optional(),
      allowedEmailDomain: z.string().nullable().optional(),
      requiredAuthProvider: z.enum(['google', 'microsoft']).nullable().optional(),
      isActive: z.boolean().optional(),
      features: organizationFeaturesSchema.partial().optional(),
      creditLimit: z.number().int().min(1).optional(),
      creditsReset: creditsResetModeSchema.optional(),
      creditsAnchorDay: z.number().int().min(1).max(28).optional(),
    })
  )
  .output(z.object({ organization: adminOrganizationSchema }))

export const adminUpdateOrgMemberContract = oc
  .input(
    z.object({
      organizationId: z.string().min(1),
      userId: z.string().min(1),
      role: z.enum(['owner', 'admin', 'member']).optional(),
      isGuest: z.boolean().optional(),
    })
  )
  .output(z.object({ ok: z.literal(true) }))

export const adminToggleOrgMemberDisabledContract = oc
  .input(
    z.object({
      organizationId: z.string().min(1),
      userId: z.string().min(1),
      disabled: z.boolean(),
    })
  )
  .output(z.object({ ok: z.literal(true) }))

// Phase 8: Org members, projects, bootstrap

export const adminOrgMemberSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(['owner', 'admin', 'member']),
  isGuest: z.boolean(),
  disabledAt: z.string().nullable(),
  joinedAt: z.string(),
  lastLoginAt: z.string().nullable(),
})

export const adminOrgProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  memberCount: z.number(),
  createdAt: z.string(),
})

export const adminListOrgMembersContract = oc
  .input(z.object({ organizationId: z.string().min(1) }))
  .output(z.object({ members: z.array(adminOrgMemberSchema) }))

export const adminListOrgProjectsContract = oc
  .input(z.object({ organizationId: z.string().min(1) }))
  .output(z.object({ projects: z.array(adminOrgProjectSchema) }))

export const adminBootstrapOrgContract = oc
  .input(
    z.object({
      organizationId: z.string().min(1),
      ownerEmail: z.string().trim().email(),
      projectName: z.string().trim().min(1).max(100),
    })
  )
  .output(z.object({ organization: adminOrganizationSchema }))

// PLAN-057: Y.Doc monitoring

export const adminOversizedYdocSchema = z.object({
  projectId: z.string(),
  roomType: z.string(),
  roomId: z.string(),
  filePath: z.string(),
  sizeBytes: z.number(),
  sizeMB: z.string(),
})

export const adminCompactRoomContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      roomType: z.enum(['chat', 'document', 'drawing', 'tasks', 'meetings']),
      roomId: z.string().min(1),
    })
  )
  .output(
    z.object({
      compacted: z.boolean(),
      originalSize: z.number(),
      compactedSize: z.number(),
    })
  )

// PLAN-058: Reset stuck chat agent

export const adminResetChatAgentContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      threadId: z.string().min(1),
      drainQueue: z.boolean().default(false),
    })
  )
  .output(
    z.object({
      reset: z.boolean(),
      queueDrained: z.number(),
    })
  )

export const adminTriggerDocumentPipelineContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      documentId: z.string().min(1),
    })
  )
  .output(
    z.object({
      triggered: z.boolean(),
      documentTitle: z.string(),
    })
  )

export const adminListOversizedYdocsContract = oc
  .input(
    z.object({
      thresholdBytes: z.number().int().min(0).default(1_048_576),
    })
  )
  .output(
    z.object({
      items: z.array(adminOversizedYdocSchema),
      totalCount: z.number(),
    })
  )

/** Super-admin "Reset usage": ledger rows before now stop counting. */
export const adminResetOrganizationCreditsContract = oc
  .input(z.object({ organizationId: z.string().min(1) }))
  .output(z.object({ organization: adminOrganizationSchema }))

// PLAN-086: nightly project exports to R2

export const adminBackupComponentSchema = z.object({
  name: z.string(),
  status: z.string(),
  count: z.number(),
  bytes: z.number(),
})

/** One project's latest export, as /admin/backups shows it. */
export const adminProjectBackupSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  organizationId: z.string().nullable(),
  organizationName: z.string().nullable(),
  /** Newest snapshot with status `complete` — null when there has never been one. */
  lastCompleteAt: z.string().nullable(),
  lastCompleteKind: z.string().nullable(),
  lastCompleteSnapshotId: z.string().nullable(),
  bytes: z.number(),
  objects: z.number(),
  /** Newest attempt of any outcome, so a run that keeps failing is visible. */
  lastAttemptAt: z.string().nullable(),
  lastAttemptStatus: z.string().nullable(),
  lastError: z.string().nullable(),
  /** Hours since the last complete export; null when there has never been one. */
  ageHours: z.number().nullable(),
  /** Older than 36 h or never — the red row. */
  stale: z.boolean(),
})

export const adminBackupRunSchema = z.object({
  id: z.string(),
  kind: z.string(),
  snapshotId: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  status: z.string(),
  projectsTotal: z.number(),
  ok: z.number(),
  partial: z.number(),
  failed: z.number(),
  projects: z.array(
    z.object({
      projectId: z.string(),
      projectName: z.string().nullable(),
      status: z.string(),
      bytes: z.number(),
      objects: z.number(),
      finishedAt: z.string().nullable(),
      error: z.string().nullable(),
      components: z.array(adminBackupComponentSchema),
    })
  ),
})

export const adminListBackupsContract = oc.input(z.void()).output(
  z.object({
    projects: z.array(adminProjectBackupSchema),
    lastRunAt: z.string().nullable(),
    lastRunStatus: z.string().nullable(),
    /** Manual runs count here — the page labels the kind. The watchdog and /health do not. */
    lastRunKind: z.string().nullable(),
  })
)

export const adminGetBackupRunsContract = oc
  .input(z.object({ limit: z.number().int().min(1).max(50).default(20) }))
  .output(z.object({ runs: z.array(adminBackupRunSchema) }))

/**
 * Run a meeting recording's transcript again (card hglk step A): fresh download
 * URL, same mapping, the notes merged again; the charge is keyed by the
 * recording, so nothing is booked twice.
 */
export const adminReplayRecordingTranscriptContract = oc
  .input(z.object({ projectId: z.string().min(1), meetingId: z.string().min(1) }))
  .output(z.object({ recordingId: z.string() }))

/** "Export now" — a manual run for the given projects, into `manual/`. */
export const adminTriggerBackupContract = oc
  .input(z.object({ projectIds: z.array(z.string().min(1)).min(1).max(50) }))
  .output(z.object({ runId: z.string(), projects: z.number() }))
